import { useMemo } from 'react'
import useItems from '~/prix/react/hooks/items'
import attendantVisitLocationsListQuery from '../../attendant/visitLocationsList/visitLocationsList.query'
import { QueryBase } from '~/prix/query'
import { isValid } from 'date-fns'
import { AgentReportValues } from '../types/agentReport.types'
import { parseDateToUTC } from '~/prix/types/date'

export interface VisitLocation {
  attendanceId: number | string
  legalEntityId: string
  legalEntityName: string
  legalEntityCoordinates?: { coordinates?: [number, number] }
  legalEntityDataSource?: number
  attendanceStartDate?: string
  serviceUnitCoordinates?: any
  cityId?: number
  legalEntitySize?: string
  isBranch?: boolean
}

export interface UseAttendantVisitLocationsParams {
  agent: { id: string; name: string }
  startDate: string
  endDate: string
  period?: { value?: string }
  autoLoad?: boolean
}

export interface UseAttendantVisitLocationsReturn {
  visitLocations: VisitLocation[]
  isLoading: boolean
  isLoadingComparison?: boolean
  error?: any
  validAttendances: VisitLocation[]
  validAttendancesComparison?: VisitLocation[]
  attendancesCount: number
}

/**
 * Hook para centralizar a query attendantVisitLocationsListQuery
 * Evita múltiplas chamadas desnecessárias da mesma query em diferentes componentes
 */
export function useAttendantVisitLocations({
  agent,
  startDate,
  endDate,
  autoLoad = false,
}: UseAttendantVisitLocationsParams): UseAttendantVisitLocationsReturn {
  const values: AgentReportValues = useMemo(
    () => ({
      attendant: { id: agent.id, name: agent.name },
      period: {},
    }),
    [agent.id, agent.name],
  )

  const visitLocationsQuery = useItems(
    () => {
      // You may want to pass a specific dataSource, here using id: 6 as default
      return agent
        ? (attendantVisitLocationsListQuery({
            ...values,
            dataSource: { id: 6 },
          }) as QueryBase)
        : (null as never)
    },
    [values],
    {
      cache: 60 * 60 * 24 * 7,
      autoLoad: !!agent.id && autoLoad,
    },
  )

  const validAttendances = useMemo((): VisitLocation[] => {
    if (!visitLocationsQuery.items) return []

    const start = parseDateToUTC(startDate)
    start.setHours(0, 0, 0, 0)
    const end = parseDateToUTC(endDate)
    end.setHours(23, 59, 59, 999)

    const uniqueMap = new Map<number, VisitLocation>()

    for (const item of visitLocationsQuery.items as unknown as VisitLocation[]) {
      let attendanceDate = item.attendanceStartDate ? new Date(item.attendanceStartDate) : null
      if (!attendanceDate || !isValid(attendanceDate)) {
        attendanceDate = new Date()
      }

      // Filtra
      const hasCoordinates = item.legalEntityCoordinates?.coordinates !== undefined
      const validDataSource = item.legalEntityDataSource === 6
      const inRange = attendanceDate >= start && attendanceDate <= end

      if (hasCoordinates && validDataSource && inRange) {
        const id = Number(item.attendanceId)
        if (!isNaN(id) && !uniqueMap.has(id)) {
          uniqueMap.set(id, item)
        }
      }
    }

    return Array.from(uniqueMap.values())
  }, [visitLocationsQuery.items, startDate, endDate])

  const attendancesCount = validAttendances.length

  return {
    visitLocations: (visitLocationsQuery.items as unknown as VisitLocation[]) || [],
    isLoading: visitLocationsQuery.isLoading,
    error: visitLocationsQuery.error,
    validAttendances,
    attendancesCount,
  }
}
